// Anchored popover primitive.
//
// A dropdown inside a scrollable panel (the schedule drawer) can't be a plain
// absolutely-positioned child: it gets clipped by the panel's overflow. Making
// it in-flow instead avoided the clipping but pushed the form around — inside a
// two-column grid it stretched one cell and shoved everything below it down.
//
// So it is portaled to <body> and positioned `fixed` against its anchor: it
// can't be clipped, it never affects layout, and it flips above the anchor when
// there isn't room below. Scrolling closes it rather than chasing the anchor,
// which is what every native select does — but only scrolling that MOVES THE
// ANCHOR (see scrollClosesPopover).

import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  /** Element to position against (the trigger). */
  anchor: HTMLElement | null;
  onClose: () => void;
  /** Take the anchor's width — the dropdown feel. */
  matchWidth?: boolean;
  className?: string;
  children: ReactNode;
}

const GAP = 6;
const EDGE = 8;

/** Surface we can ask "is this node inside you?" — a DOM node in the app, a
 * stub in the test. */
interface ScrollSurface {
  contains(node: Node | null): boolean;
}

/**
 * Whether a scroll event should dismiss the popover.
 *
 * Closing on scroll exists because a `fixed` popover would otherwise float away
 * from an anchor that scrolled out from under it. That reasoning only covers
 * scrolls OUTSIDE the popover. A scroll INSIDE it is ordinary interaction — a
 * long option list scrolls, and TimeSelect scrolls its list to the selected
 * time the instant it opens. Treating that as "the page moved" made the time
 * dropdowns flash open and vanish in the same frame: open → list scrolls to
 * 10:00 → close.
 */
export function scrollClosesPopover(surface: ScrollSurface | null, target: EventTarget | null): boolean {
  if (!surface || !target) return true; // document-level scroll: the anchor moved
  return !surface.contains(target as Node);
}

export function Popover({ anchor, onClose, matchWidth = false, className, children }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const place = useCallback(() => {
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const h = ref.current?.offsetHeight ?? 0;
    const below = window.innerHeight - r.bottom;
    // Flip up only when it genuinely doesn't fit below AND there's more room up.
    const flip = below < h + GAP + EDGE && r.top > below;
    const width = matchWidth ? r.width : (ref.current?.offsetWidth ?? r.width);
    setPos({
      top: flip ? Math.max(EDGE, r.top - h - GAP) : r.bottom + GAP,
      left: Math.min(Math.max(EDGE, r.left), Math.max(EDGE, window.innerWidth - width - EDGE)),
      width: r.width,
    });
  }, [anchor, matchWidth]);

  // Place before paint, then once more after the content has a real height.
  useLayoutEffect(() => {
    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', place);
    };
  }, [place]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !anchor?.contains(t)) onClose();
    };
    const onScroll = (e: Event) => {
      if (scrollClosesPopover(ref.current, e.target)) onClose();
    };
    // Capture scroll from ANY container (the drawer body included) — except the
    // popover's own, which is the user reading the list, not the anchor moving.
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown, true);
    };
  }, [anchor, onClose]);

  const style = useMemo(
    () =>
      pos
        ? {
            top: `${pos.top}px`,
            left: `${pos.left}px`,
            ...(matchWidth ? { width: `${pos.width}px` } : null),
          }
        : // Measure off-screen on the first pass so the flip decision sees a
          // real height instead of 0.
          { top: '-9999px', left: '-9999px' },
    [pos, matchWidth],
  );

  return createPortal(
    <div ref={ref} className={className ? `bz-popover ${className}` : 'bz-popover'} style={style}>
      {children}
    </div>,
    document.body,
  );
}
